<template>
  <!-- Template Selector -->
  <div class="d-flex align-items-center flex-grow-1">
    <v-select
      ref="templateSelect"
      class="v-select-template flex-grow-1"
      v-model="selectedTemplate"
      :options="templateOptions"
      :reduce="option => option.value"
      :clearable="true"
      placeholder="Apply option group templates from..."
      @input="applyTemplateOptionGroups"
    >
      <template #option="{label, source, usage_count}">
        <div class="d-flex align-items-center">
          <CIcon name="cil-list-rich" class="mr-2" />
          <div>
            <div class="d-flex align-items-center">
              {{ label }}
              <CBadge color="info" class="ml-2">Usage: {{ usage_count }}</CBadge>
            </div>
            <small v-if="source" class="text-muted">{{ source }}</small>
          </div>
        </div>
      </template>
      <template #selected-option="{label, source}">
        <div class="d-flex align-items-center">
          <CIcon name="cil-list-rich" class="mr-2" />
          <div>
            <div class="d-flex align-items-center">
              {{ isCustomCollection ? 'Custom Collection' : label }}
              <CBadge v-if="!isCustomCollection" color="info" class="ml-2">Usage: {{ templateOptions.find(t => t.value === selectedTemplate)?.usage_count }}</CBadge>
            </div>
            <small v-if="source && !isCustomCollection" class="text-muted">{{ source }}</small>
          </div>
        </div>
      </template>
    </v-select>
  </div>
</template>

<script>
export default {
  name: 'OptionGroupTemplates',

  props: {
    restaurantId: { type: [String, Number], required: true },
    selectedVariantId: { type: [String, Number, null], default: null },
    selectedMenuOptionGroups: { type: Array, default: () => [] },
    variants: { type: Array, default: () => [] }
  },

  data() {
    return {
      menuTemplates: [],
      selectedTemplate: null,
      isCustomCollection: false,
      appliedTemplateGroups: null,
      applyToAllVariants: false,
    };
  },

  computed: {
    templateOptions() {
      if (!this.menuTemplates || this.menuTemplates.length === 0) {
        return [];
      }

      return this.menuTemplates.map(template => ({
        label: template.name,
        value: template.id,
        source: template.source,
        option_group_ids: template.option_group_ids,
        usage_count: template.usage_count
      }));
    },
    hasVariants() { return this.variants && this.variants.length > 0 },
  },

  watch: {
    selectedMenuOptionGroups: {
      deep: true,
      handler() {
        this.checkIfCustomCollection();
      }
    }
  },

  methods: {
    /**
     * Fetch option group templates by category
     * @param {Number} category_id - Category ID to filter templates
     */
    async fetchOptionGroupTemplates(category_id) {
      try {
        // Use a custom endpoint for option group templates
        const url = `${this.$backend.MENU_V2.OPTION_GROUP_TEMPLATES(this.restaurantId)}`;
        const response = await this.$axios.get(url, { params: { category_id } });

        // Check response structure - backend might return {templates, debug} or just templates array
        if (response.data && response.data.templates) {
          this.menuTemplates = response.data.templates;
        } else {
          // Production response format - direct array
          this.menuTemplates = response.data;
        }

        // Reset template selection when templates change
        this.selectedTemplate = null;
        this.isCustomCollection = false;

        // Emit updated templates
        this.$emit('templates-loaded', this.menuTemplates);
      } catch (error) {
        this.menuTemplates = [];
      }
    },

    /**
     * Check if the current option groups collection has been modified from the original template
     */
    checkIfCustomCollection() {
      // Sadece bir şablon uygulanmışsa ve kaydedilmiş option grupları varsa kontrol et
      if (this.selectedTemplate && this.appliedTemplateGroups) {
        // Mevcut varyant için option grupları al
        const currentOptionGroupIds = this.selectedMenuOptionGroups
          .filter(item => item.variantId === this.selectedVariantId)
          .map(item => item.optionGroupId)
          .sort((a, b) => a - b);

        // Kayıtlı template option grupları
        const appliedGroupIds = [...this.appliedTemplateGroups].sort((a, b) => a - b);

        // Eğer mevcut ve uygulanan option grup listeleri farklıysa custom collection olarak işaretle
        if (currentOptionGroupIds.length !== appliedGroupIds.length) {
          this.isCustomCollection = true;
          return;
        }

        // İçerik karşılaştırması
        for (let i = 0; i < currentOptionGroupIds.length; i++) {
          if (currentOptionGroupIds[i] !== appliedGroupIds[i]) {
            this.isCustomCollection = true;
            return;
          }
        }

        // Değişiklik yok, özel koleksiyon değil
        this.isCustomCollection = false;
      }
    },

    /**
     * Apply option groups from the selected template
     * @param {Number} templateId - ID of the selected template menu
     */
    async applyTemplateOptionGroups(templateId) {
      if (!templateId) {
        return;
      }

      const template = this.menuTemplates.find(t => t.id === parseInt(templateId));
      if (!template) {
        return;
      }

      // Confirm before applying template
      const confirmResult = await this.mSwal.fire({
        title: 'Apply Option Group Template',
        html: `
          <div class="text-left">
            <p>This will replace all current option groups with:</p>
            <div class="alert alert-info mb-0 p-2">
              <ol class="my-1">
                ${template.name.split(' | ').map(group => `<li>${group}</li>`).join('')}
              </ol>
            </div>
            <div class="mt-3">
              <div><strong>Source:</strong> ${template.source}</div>
              <div><strong>Usage Count:</strong> ${template.usage_count}</div>
            </div>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, apply template',
        cancelButtonText: 'Cancel',
        input: this.hasVariants ? 'checkbox' : undefined,
        inputValue: 0,
        inputPlaceholder: 'Apply to all variants',
        customClass: {
          input: 'swal2-checkbox-custom'
        }
      });

      if (!confirmResult.isConfirmed) {
        // Seçimi geri al
        this.selectedTemplate = null;
        return;
      }

      // Option group IDs to apply from the template
      let optionGroupIds = [];
      if (template.option_group_ids && Array.isArray(template.option_group_ids)) {
        optionGroupIds = template.option_group_ids;
      } else if (template.optionGroupIds && Array.isArray(template.optionGroupIds)) {
        optionGroupIds = template.optionGroupIds;
      } else {
        this.$toast.error('No valid option groups found in template');
        return;
      }

      // Emit event to parent component to update the option groups
      if (confirmResult.value && this.hasVariants) {
        // Tüm varyantlara uygula
        this.variants.forEach(variant => {
          this.$emit('apply-template', {
            optionGroupIds,
            variantId: variant.id
          });
        });
      } else {
        // Sadece seçili varyanta uygula
        this.$emit('apply-template', {
          optionGroupIds,
          variantId: this.selectedVariantId
        });
      }

      // Uygulanan şablonu kaydet
      this.appliedTemplateGroups = [...optionGroupIds];

      // Template uygulandığında custom collection değil
      this.isCustomCollection = false;

      this.$toast.success(`Applied option groups template: "${template.name}"`);
    }
  }
}
</script>

<style>
.v-select-template .vs__dropdown-toggle {
  box-shadow: 0 0 0.05em #fff, 0 0 0.2em #d60b52, 0 0 0.6em #d60b52;
}

.vs--single .vs__selected {
  font-weight: 500 !important;
  font-size: 1rem !important;
  line-height: 1.7 !important;
}

.v-select-template .vs__search {
  line-height: 1.7;
}

.v-select-template .vs__clear {
  margin-top: -3px;
}

/* SweetAlert2 Custom Checkbox Styles */
.swal2-checkbox-custom {
  padding: 0.5rem 1rem !important;
  border-radius: 0.5rem !important;
  background-color: #f8f9fa !important;
  border: 2px solid #e9ecef !important;
  transition: all 0.2s ease-in-out !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
}

.swal2-checkbox-custom:hover {
  background-color: #e9ecef !important;
  border-color: #dee2e6 !important;
}

.swal2-checkbox-custom:checked {
  background-color: #d60b52 !important;
  border-color: #d60b52 !important;
  color: white !important;
}

.swal2-checkbox-custom:checked + .swal2-label {
  color: white !important;
}

.swal2-checkbox-custom:focus {
  box-shadow: 0 0 0 0.2rem rgba(214, 11, 82, 0.25) !important;
  outline: none !important;
}
</style>
